import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AnalyticsWarehousePicker from "../../components/analytics-warehouse-picker";
import ClientAutocomplete from "../../components/clientAutocomplete";
import AnalysisPageTopbar from "../../components/metrics/AnalysisPageTopbar";
import SalesPerformancePageLoading from "../../components/metrics/SalesPerformancePageLoading";
import {
  MONO_FONT,
  PAGE_FONT,
  headCellSx,
  numCellSx,
  textCellSx,
  useMetricsUi,
} from "../../components/metrics/metricsUi";
import { isMobile } from "../../globalStyles/mobile";
import { inventoryHealthService } from "../../utils/services/inventoryHealthService";
import { analyticsWarehouseService } from "../../utils/services/analyticsWarehouseService";
import {
  AUDIT_VIEW,
  canRunInventoryHealth,
  resolveAuditViewState,
  auditUnavailableCopy,
  auditEmptyState,
  selectableWarehouse,
} from "../../utils/inventoryHealthPageDefaults";

const FIELD_HEIGHT = 40;

const FLAG_LABELS = {
  missing_start: "Missing start",
  missing_end: "Missing end",
  missing_data: "Missing Data",
  duplicate_snapshot: "Duplicate snapshot",
  conflicting_snapshot: "Conflicting snapshot",
  provider_collapse: "Provider collapse",
  no_lot_qty: "No lot qty",
  stock_in_no_lot: "Stock-in, no lot",
  po_unmapped: "PO unmapped",
  ambiguous_sales: "Ambiguous sales",
  source_gap: "Source gap",
};

const METHOD_NOTE =
  "This audit reconciles start-of-day snapshots against fulfilled order demand and physically received inventory. A non-zero Delta is a data-quality discrepancy, not shrinkage. Ordered/accepted PO quantities are never treated as received.";

const eyebrowSx = (colors) => ({
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: colors.textSecondary,
});

const FieldLabel = ({ children, colors }) => (
  <Typography
    sx={{
      ...eyebrowSx(colors),
      mb: 0.65,
      fontFamily: PAGE_FONT,
      lineHeight: 1.3,
    }}
  >
    {children}
  </Typography>
);

const formatDay = (value) => {
  if (!value) return "—";
  const raw = typeof value === "string" ? value.slice(0, 10) : value;
  const date = raw instanceof Date ? raw : new Date(`${raw}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return String(raw);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
};

const fmtUnits = (value) => {
  if (value == null || !Number.isFinite(Number(value))) return "—";
  return Math.round(Number(value)).toLocaleString("en-US");
};

const missingSnapshotLabel = (row) => {
  if (row?.missing_start && row?.missing_end) {
    return "Starting and ending snapshots";
  }
  if (row?.missing_start) return "Starting snapshot";
  if (row?.missing_end) return "Ending snapshot";
  return "Snapshot unavailable";
};

const fmtDelta = (value) => {
  if (value == null || !Number.isFinite(Number(value))) return "—";
  const number = Math.round(Number(value));
  const abs = Math.abs(number).toLocaleString("en-US");
  if (number > 0) return `+${abs}`;
  if (number < 0) return `−${abs}`;
  return "0";
};

const TONE_ICON = {
  warning: WarningAmberOutlinedIcon,
  error: ErrorOutlineIcon,
  success: CheckCircleOutlineIcon,
  info: InfoOutlinedIcon,
};

/**
 * Deliberate empty/unavailable/error state: one centered, balanced block that
 * reads as designed page content rather than an alert dropped into the layout.
 */
const StatePanel = ({ tone = "info", eyebrow, title, body, hint, ui }) => {
  const { colors } = ui;
  const Icon = TONE_ICON[tone] || InfoOutlinedIcon;
  const accent =
    tone === "warning"
      ? ui.warning
      : tone === "error"
        ? ui.danger
        : tone === "success"
          ? ui.positive
          : ui.accent;
  const accentBg =
    tone === "warning"
      ? ui.warningBg
      : tone === "error"
        ? ui.dangerBg
        : tone === "success"
          ? ui.positiveBg
          : ui.accentSoft;

  return (
    <Box
      sx={{
        ...ui.card,
        px: 3,
        py: 4.5,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: "11px",
          backgroundColor: accentBg,
          color: accent,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 1.75,
        }}
      >
        <Icon sx={{ fontSize: 21 }} />
      </Box>

      {eyebrow ? (
        <Typography sx={{ ...eyebrowSx(colors), color: accent, mb: 0.85 }}>
          {eyebrow}
        </Typography>
      ) : null}

      <Typography
        sx={{
          fontSize: 15,
          fontWeight: 650,
          color: colors.textPrimary,
          maxWidth: 520,
          lineHeight: 1.4,
        }}
      >
        {title}
      </Typography>

      {body ? (
        <Typography
          sx={{
            fontSize: 13,
            color: colors.textSecondary,
            maxWidth: 520,
            mt: 0.85,
            lineHeight: 1.55,
          }}
        >
          {body}
        </Typography>
      ) : null}

      {hint ? (
        <Typography
          sx={{
            fontSize: 12,
            color: colors.textTertiary,
            maxWidth: 520,
            mt: 1.5,
            lineHeight: 1.5,
          }}
        >
          {hint}
        </Typography>
      ) : null}
    </Box>
  );
};

const KpiCard = ({ label, value, hint, ui }) => (
  <Box
    sx={{
      ...ui.card,
      p: "13px 15px 12px",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      transition: "border-color .12s",
      "&:hover": { borderColor: ui.borderStrong },
    }}
  >
    <Typography sx={{ ...eyebrowSx(ui.colors), lineHeight: 1.35 }}>
      {label}
    </Typography>
    <Typography
      sx={{
        fontFamily: MONO_FONT,
        fontSize: 23,
        fontWeight: 650,
        letterSpacing: "-0.01em",
        fontVariantNumeric: "tabular-nums",
        color: ui.colors.textPrimary,
        mt: 0.75,
        lineHeight: 1.15,
      }}
    >
      {value ?? "—"}
    </Typography>
    <Typography
      sx={{
        fontSize: 11,
        color: ui.colors.textTertiary,
        mt: "auto",
        pt: 0.9,
        lineHeight: 1.35,
      }}
    >
      {hint}
    </Typography>
  </Box>
);

const PeriodFact = ({ label, value, mono = true, ui }) => (
  <Box>
    <Typography sx={{ ...eyebrowSx(ui.colors), mb: 0.4 }}>{label}</Typography>
    <Typography
      sx={{
        fontSize: 13,
        fontFamily: mono ? MONO_FONT : PAGE_FONT,
        color: ui.colors.textPrimary,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

const SectionCard = ({ title, meta, footnote, children, ui, sx }) => (
  <Box sx={{ ...ui.card, overflow: "hidden", ...sx }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: 1,
        flexWrap: "wrap",
        px: 2,
        pt: 1.75,
        pb: 1.5,
      }}
    >
      <Typography
        sx={{ fontSize: 14, fontWeight: 700, color: ui.colors.textPrimary }}
      >
        {title}
      </Typography>
      {meta ? (
        <Typography sx={{ fontSize: 12, color: ui.colors.textSecondary }}>
          {meta}
        </Typography>
      ) : null}
    </Box>
    <Box sx={{ overflowX: "auto" }}>{children}</Box>
    {footnote ? (
      <Typography
        sx={{
          fontSize: 11.5,
          color: ui.colors.textTertiary,
          borderTop: `1px solid ${ui.colors.border}`,
          px: 2,
          py: 1.35,
          lineHeight: 1.5,
        }}
      >
        {footnote}
      </Typography>
    ) : null}
  </Box>
);

const InventoryHealthPage = () => {
  const ui = useMetricsUi();
  const { colors, darkMode } = ui;
  const mobile = isMobile();
  const storedClientId = localStorage.getItem("client_id");
  const [selectedClientData, setSelectedClientData] = useState(null);
  const clientId = selectedClientData?._id || storedClientId;

  const [warehouse, setWarehouse] = useState(null);
  // Results are stamped with the warehouse they belong to so a superseded or
  // in-flight request can never render the previous warehouse's outcome.
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [warehousesLoading, setWarehousesLoading] = useState(
    Boolean(storedClientId),
  );
  const [warehousesEmpty, setWarehousesEmpty] = useState(false);
  const [error, setError] = useState("");
  const warehouseSeq = useRef(0);
  const runSeq = useRef(0);

  const warehouseId = warehouse && warehouse._id;
  const canRun = canRunInventoryHealth({
    clientId,
    warehouse,
    warehousesLoading,
  });

  useEffect(() => {
    if (!clientId) {
      warehouseSeq.current += 1;
      setWarehouse(null);
      setWarehousesEmpty(false);
      setWarehousesLoading(false);
      setResult(null);
      return undefined;
    }
    const seq = ++warehouseSeq.current;
    setWarehouse(null);
    setWarehousesEmpty(false);
    setWarehousesLoading(true);
    setResult(null);
    setError("");
    // Default analytics warehouse when offered, else the first offered one.
    analyticsWarehouseService
      .fetchDefault(clientId)
      .then((preferred) => {
        if (seq !== warehouseSeq.current) return;
        const next = selectableWarehouse(preferred);
        setWarehouse(next);
        setWarehousesEmpty(!next);
      })
      .catch((err) => {
        if (seq !== warehouseSeq.current) return;
        setWarehouse(null);
        setWarehousesEmpty(false);
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Unable to load warehouses.",
        );
      })
      .finally(() => {
        if (seq === warehouseSeq.current) setWarehousesLoading(false);
      });
    return undefined;
  }, [clientId]);

  const run = useCallback(async () => {
    if (!canRun) return;
    const requestedWarehouseId = warehouse._id;
    const seq = ++runSeq.current;
    setResult(null);
    setLoading(true);
    setError("");
    try {
      const data = await inventoryHealthService.fetchAudit({
        clientId,
        warehouseId: requestedWarehouseId,
      });
      if (seq !== runSeq.current) return;
      setResult({ warehouseId: requestedWarehouseId, data });
    } catch (err) {
      if (seq !== runSeq.current) return;
      setResult(null);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to load inventory health audit.",
      );
    } finally {
      if (seq === runSeq.current) setLoading(false);
    }
  }, [canRun, clientId, warehouse]);

  useEffect(() => {
    if (!canRun) {
      runSeq.current += 1;
      setResult(null);
      setLoading(false);
      return undefined;
    }
    run();
    return undefined;
  }, [canRun, clientId, warehouseId, run]);

  const viewState = resolveAuditViewState({
    clientId,
    warehouseId,
    warehousesLoading,
    warehousesEmpty,
    loading,
    error,
    payload: result?.data,
    payloadWarehouseId: result?.warehouseId,
  });

  // Only a result that belongs to the currently selected warehouse is rendered.
  const payload =
    viewState === AUDIT_VIEW.SUCCESS || viewState === AUDIT_VIEW.UNAVAILABLE
      ? result.data
      : null;
  const period = payload?.period;
  const freshness = payload?.freshness;
  const summary = payload?.summary;
  const rows = payload?.rows || [];
  const missingRows = payload?.missing_rows || [];
  const unavailableCopy =
    viewState === AUDIT_VIEW.UNAVAILABLE ? auditUnavailableCopy(payload) : null;
  const emptyState =
    viewState === AUDIT_VIEW.SUCCESS ? auditEmptyState(payload) : null;

  const periodCopy = useMemo(() => {
    if (!period) return null;
    const auditDays = (period.audit_days || []).map(formatDay).join(", ");
    return {
      range: `${formatDay(period.start_day)} → ${formatDay(period.end_day)}`,
      audit: auditDays || formatDay(period.start_day),
      sales: formatDay(freshness?.latest_sales_day),
      start: period.starting_snapshot,
      end: period.ending_snapshot,
    };
  }, [period, freshness]);

  const warnings = payload?.warnings || [];
  const freshnessWarning = warnings.find((text) =>
    /Sales data last updated|treated as 0 for the audit period/.test(text),
  );
  const otherWarnings = warnings.filter((text) => text !== freshnessWarning);

  const deltaColor = (delta) => {
    const number = Number(delta);
    if (!Number.isFinite(number) || number === 0) return colors.textTertiary;
    return number < 0 ? ui.danger : ui.warning;
  };

  return (
    <Box
      sx={{
        minHeight: "100%",
        backgroundColor: darkMode ? colors.bgPrimary : "#F5F6F8",
        fontFamily: PAGE_FONT,
      }}
    >
      {viewState === AUDIT_VIEW.LOADING && (
        <LinearProgress
          sx={{ position: "sticky", top: 0, zIndex: 10, height: 3 }}
        />
      )}
      <Box
        sx={{
          maxWidth: 1480,
          mx: "auto",
          padding: mobile ? "14px 12px 40px" : "22px 28px 56px",
        }}
      >
        <AnalysisPageTopbar
          badge="IH"
          title="Inventory Health"
          clientName={
            selectedClientData?.business_name || selectedClientData?.name
          }
          right={
            !storedClientId ? (
              <ClientAutocomplete
                width={220}
                selectedClient={selectedClientData}
                onClientChange={setSelectedClientData}
              />
            ) : null
          }
        />

        <Box
          sx={{
            display: "flex",
            gap: 1.25,
            alignItems: "flex-start",
            backgroundColor: ui.surfaceAlt,
            border: `1px solid ${colors.border}`,
            borderRadius: "12px",
            px: 1.75,
            py: 1.35,
            mb: 2.5,
          }}
        >
          <InfoOutlinedIcon
            sx={{ fontSize: 17, color: colors.textTertiary, mt: "1px" }}
          />
          <Typography
            sx={{
              fontSize: 12.5,
              color: colors.textSecondary,
              lineHeight: 1.55,
            }}
          >
            {METHOD_NOTE}
          </Typography>
        </Box>

        <Box
          sx={{
            ...ui.card,
            p: mobile ? "14px 14px 16px" : "16px 18px 18px",
            mb: 2.5,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: mobile ? "1fr" : "minmax(220px, 1.6fr) auto",
              gap: 1.5,
              alignItems: "end",
            }}
          >
            <Box>
              <FieldLabel colors={colors}>Warehouse</FieldLabel>
              <AnalyticsWarehousePicker
                clientId={clientId}
                mode="single"
                allowAll={false}
                value={warehouse}
                onChange={(item) => setWarehouse(selectableWarehouse(item))}
                placeholder={
                  warehousesLoading ? "Loading warehouses..." : "Select a warehouse"
                }
                disabled={!clientId || warehousesLoading}
                width="100%"
                height={FIELD_HEIGHT}
                triggerSx={{ fontFamily: PAGE_FONT, fontSize: 14, fontWeight: 400 }}
              />
            </Box>
            <Button
              variant="contained"
              disableElevation
              onClick={run}
              disabled={!canRun || loading}
              sx={{ height: FIELD_HEIGHT, textTransform: "none", px: 3 }}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {viewState === AUDIT_VIEW.NO_CLIENT && (
          <StatePanel
            ui={ui}
            tone="info"
            eyebrow="Client required"
            title="Select a client to run inventory health."
            body="The audit reconciles snapshots, fulfilled demand, and receipts for a single client and warehouse."
          />
        )}

        {viewState === AUDIT_VIEW.NO_WAREHOUSE && (
          <StatePanel
            ui={ui}
            tone="info"
            eyebrow="Warehouse required"
            title={
              warehousesEmpty
                ? "No warehouses were found for this client."
                : "Select a warehouse to run inventory health."
            }
            body={
              warehousesEmpty
                ? "Inventory health is audited per warehouse, so at least one warehouse must exist for this client."
                : "Choose a warehouse above to load its reconciliation."
            }
          />
        )}

        {viewState === AUDIT_VIEW.ERROR && (
          <StatePanel
            ui={ui}
            tone="error"
            eyebrow="Audit could not be loaded"
            title={error}
            hint="Use Refresh to try again, or select a different warehouse."
          />
        )}

        {viewState === AUDIT_VIEW.LOADING && (
          <SalesPerformancePageLoading
            title={
              warehouse?.warehouse_code
                ? `Loading inventory health for ${warehouse.warehouse_code}…`
                : "Loading inventory health…"
            }
            kpiCount={5}
          />
        )}

        {viewState === AUDIT_VIEW.UNAVAILABLE && unavailableCopy && (
          <StatePanel
            ui={ui}
            tone="warning"
            eyebrow={unavailableCopy.heading}
            title={unavailableCopy.title}
            body={unavailableCopy.body}
            hint="Select a different warehouse, or retry once more inventory snapshots have been recorded."
          />
        )}

        {viewState === AUDIT_VIEW.SUCCESS && (
          <>
            {periodCopy && (
              <Box
                sx={{
                  ...ui.card,
                  p: mobile ? "14px" : "16px 18px 18px",
                  mb: 2,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: colors.textPrimary,
                    mb: 1.75,
                  }}
                >
                  Audit period
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: mobile
                      ? "repeat(2, minmax(0, 1fr))"
                      : "repeat(5, minmax(0, 1fr))",
                    gap: mobile ? 1.75 : 2,
                  }}
                >
                  <PeriodFact ui={ui} label="Period" value={periodCopy.range} />
                  <PeriodFact
                    ui={ui}
                    label="Movement day(s)"
                    value={periodCopy.audit}
                  />
                  <PeriodFact
                    ui={ui}
                    label="Starting inventory"
                    value={periodCopy.start}
                  />
                  <PeriodFact
                    ui={ui}
                    label="Ending inventory"
                    value={periodCopy.end}
                  />
                  <PeriodFact
                    ui={ui}
                    label="Sales freshness"
                    value={periodCopy.sales}
                  />
                </Box>

                {freshnessWarning ? (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1.1,
                      alignItems: "flex-start",
                      mt: 2,
                      px: 1.5,
                      py: 1.15,
                      borderRadius: "10px",
                      backgroundColor: ui.warningBg,
                    }}
                  >
                    <WarningAmberOutlinedIcon
                      sx={{ fontSize: 16, color: ui.warning, mt: "1px" }}
                    />
                    <Typography
                      sx={{
                        fontSize: 12.5,
                        color: ui.warning,
                        fontWeight: 500,
                        lineHeight: 1.5,
                      }}
                    >
                      {freshnessWarning}
                    </Typography>
                  </Box>
                ) : null}

                {otherWarnings.length ? (
                  <Typography
                    sx={{
                      fontSize: 11.5,
                      color: colors.textTertiary,
                      mt: 1.75,
                      pt: 1.5,
                      borderTop: `1px solid ${colors.border}`,
                      lineHeight: 1.55,
                    }}
                  >
                    {otherWarnings.join(" ")}
                  </Typography>
                ) : null}
              </Box>
            )}

            {summary && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: mobile
                    ? "repeat(2, minmax(0, 1fr))"
                    : "repeat(5, minmax(0, 1fr))",
                  gap: 1.5,
                  mb: 2,
                }}
              >
                <KpiCard
                  ui={ui}
                  label="Positions audited"
                  value={fmtUnits(
                    summary.positions_audited ?? summary.sku_count,
                  )}
                  hint="Both snapshots present"
                />
                <KpiCard
                  ui={ui}
                  label="Non-zero Delta"
                  value={fmtUnits(summary.exception_count)}
                  hint="Shown below"
                />
                <KpiCard
                  ui={ui}
                  label="Delta = 0"
                  value={fmtUnits(summary.zero_delta_count)}
                  hint="Reconciled, hidden"
                />
                <KpiCard
                  ui={ui}
                  label="Abs. discrepancy"
                  value={fmtUnits(summary.abs_discrepancy)}
                  hint="Units, absolute"
                />
                <KpiCard
                  ui={ui}
                  label="Missing Data"
                  value={fmtUnits(summary.missing_data ?? 0)}
                  hint="Excluded from reconciliation"
                />
              </Box>
            )}

            {emptyState && (
              <StatePanel
                ui={ui}
                tone={emptyState.severity}
                title={emptyState.title}
                body={emptyState.body}
              />
            )}

            {rows.length > 0 && (
              <SectionCard
                ui={ui}
                title="Reconciliation discrepancies"
                meta={`${fmtUnits(rows.length)} position${
                  rows.length === 1 ? "" : "s"
                } with a non-zero Delta`}
                footnote="Sales are fulfilled demand (ordered line quantity on the first Fulfilled trail date)."
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headCellSx(ui, { pl: 2 })}>SKU</TableCell>
                      {[
                        "Starting",
                        "Sales",
                        "Received",
                        "Expected",
                        "Actual ending",
                        "Delta",
                      ].map((label) => (
                        <TableCell
                          key={label}
                          sx={headCellSx(ui, { textAlign: "right" })}
                        >
                          {label}
                        </TableCell>
                      ))}
                      <TableCell sx={headCellSx(ui, { pr: 2 })}>Flags</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow
                        key={row.sku}
                        sx={{
                          "&:hover": { backgroundColor: ui.surfaceAlt },
                          "&:last-of-type td": { borderBottom: 0 },
                        }}
                      >
                        <TableCell
                          sx={textCellSx(ui, {
                            fontFamily: MONO_FONT,
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                            pl: 2,
                          })}
                        >
                          {row.sku}
                        </TableCell>
                        <TableCell sx={numCellSx(ui, { fontFamily: MONO_FONT })}>
                          {fmtUnits(row.starting_inventory)}
                        </TableCell>
                        <TableCell sx={numCellSx(ui, { fontFamily: MONO_FONT })}>
                          {fmtUnits(row.sales)}
                        </TableCell>
                        <TableCell sx={numCellSx(ui, { fontFamily: MONO_FONT })}>
                          {fmtUnits(row.received)}
                        </TableCell>
                        <TableCell sx={numCellSx(ui, { fontFamily: MONO_FONT })}>
                          {fmtUnits(row.expected_ending)}
                        </TableCell>
                        <TableCell sx={numCellSx(ui, { fontFamily: MONO_FONT })}>
                          {fmtUnits(row.actual_ending)}
                        </TableCell>
                        <TableCell
                          sx={numCellSx(ui, {
                            fontFamily: MONO_FONT,
                            fontWeight: 650,
                            color: deltaColor(row.delta),
                          })}
                        >
                          {fmtDelta(row.delta)}
                        </TableCell>
                        <TableCell sx={textCellSx(ui, { pr: 2 })}>
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.5,
                            }}
                          >
                            {(row.flags || []).map((flag) => (
                              <Box
                                key={flag}
                                component="span"
                                sx={{
                                  fontSize: 10.5,
                                  fontWeight: 600,
                                  lineHeight: 1.6,
                                  color: colors.textSecondary,
                                  backgroundColor: ui.flatBg,
                                  border: `1px solid ${colors.border}`,
                                  borderRadius: "6px",
                                  px: 0.7,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {FLAG_LABELS[flag] || flag}
                              </Box>
                            ))}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </SectionCard>
            )}

            {missingRows.length > 0 && (
              <SectionCard
                ui={ui}
                title="Missing data"
                meta={`${fmtUnits(missingRows.length)} position${
                  missingRows.length === 1 ? "" : "s"
                } without a complete snapshot pair`}
                footnote="Missing historical inventory snapshots are unavailable, not zero, and are excluded from reconciliation."
                sx={{ mt: rows.length ? 2 : 0 }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headCellSx(ui, { pl: 2 })}>SKU</TableCell>
                      <TableCell sx={headCellSx(ui, { pr: 2 })}>
                        Missing
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {missingRows.map((row) => (
                      <TableRow
                        key={row.sku}
                        sx={{
                          "&:hover": { backgroundColor: ui.surfaceAlt },
                          "&:last-of-type td": { borderBottom: 0 },
                        }}
                      >
                        <TableCell
                          sx={textCellSx(ui, {
                            fontFamily: MONO_FONT,
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                            pl: 2,
                          })}
                        >
                          {row.sku}
                        </TableCell>
                        <TableCell
                          sx={textCellSx(ui, {
                            color: colors.textSecondary,
                            pr: 2,
                          })}
                        >
                          {missingSnapshotLabel(row)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </SectionCard>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default InventoryHealthPage;
